import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template3 = () => {
    const navigate = useNavigate();

    return (
        <div className="bg-white min-h-screen font-sans">
            <header className="bg-white shadow-sm p-4 relative z-10">
                <div className="container mx-auto flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                    >
                        <ArrowLeft size={18} /> Back to Templates
                    </button>
                    <h1 className="text-3xl font-bold text-center">Banner Store</h1>
                    <div className="w-24"></div> {/* Placeholder for alignment */}
                </div>
            </header>

            <main>
                {/* Hero Section - Replicated from DesignerPage3 */}
                <section className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] mb-16 rounded-3xl overflow-hidden shadow-xl">
                    <img 
                        src="https://placehold.co/1920x1080/444444/FFFFFF?text=Hero+Banner" 
                        alt="Banner" 
                        className="absolute inset-0 w-full h-full object-cover" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-tr from-black/70 via-black/40 to-transparent" />
                    <div className="relative z-10 h-full flex flex-col justify-center items-start px-6 sm:px-12 lg:px-20 text-white">
                        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-extrabold mb-4 drop-shadow-md">Unleash Your Style</h1>
                        <p className="text-base sm:text-lg lg:text-xl mb-6 max-w-xl text-white/90">
                            Discover the latest trends in Indian, Western, and Kids wear — all in one place.
                        </p>
                        <a href="#" className="bg-white text-black font-semibold px-5 py-3 rounded-full shadow hover:bg-gray-100 transition">Explore Collection</a>
                    </div>
                </section>

                {/* Profile Section - Replicated from DesignerPage3 */}
                <section className="max-w-7xl mx-auto px-4 py-8">
                    <div className="flex flex-col lg:flex-row items-center gap-10">
                        <div className="relative">
                            <img src="https://placehold.co/200x200/E0E0E0/333333?text=Profile" className="w-52 h-52 sm:w-72 sm:h-72 object-cover rounded-3xl shadow-2xl transition-transform duration-500 hover:scale-105" alt="Designer Profile" />
                        </div>
                        <div className="flex-1 text-center lg:text-left">
                            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4">Designer's Name</h1>
                            <p className="text-slate-600 text-lg sm:text-xl max-w-2xl mx-auto lg:mx-0">
                                A clean, timeless layout perfect for any store.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Category Carousel - Replicated from DesignerPage3 */}
                <section className="max-w-7xl mx-auto px-4 py-8">
                    <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 lg:mb-12 text-slate-900">
                        Shop by Category
                    </h2>
                    <div className="flex overflow-x-auto space-x-6 pb-4">
                        {[
                            { name: "Indianwear", img: "https://placehold.co/400x500/A0A0A0/FFFFFF?text=Indianwear" },
                            { name: "Westernwear", img: "https://placehold.co/400x500/B0B0B0/FFFFFF?text=Westernwear" },
                            { name: "Blazers", img: "https://placehold.co/400x500/C0C0C0/FFFFFF?text=Blazers" },
                            { name: "Kurtis", img: "https://placehold.co/400x500/D0D0D0/FFFFFF?text=Kurtis" },
                            { name: "Accessories", img: "https://placehold.co/400x500/E0E0E0/FFFFFF?text=Accessories" }
                        ].map((cat) => (
                            <div key={cat.name} className="group relative overflow-hidden rounded-2xl lg:rounded-3xl shadow-xl hover:shadow-3xl transform transition-all duration-500 hover:scale-105 w-72 md:w-80 flex-shrink-0">
                                <div className="aspect-[4/5] relative">
                                    <img src={cat.img} alt={cat.name} className="absolute inset-0 w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-black/60 group-hover:bg-black/40 transition-colors duration-300" />
                                    <div className="absolute bottom-0 w-full p-4 bg-gradient-to-t from-black/60 via-transparent to-transparent text-white">
                                        <h3 className="text-xl sm:text-2xl font-bold">{cat.name}</h3>
                                        <div className="mt-1 text-xs sm:text-sm flex items-center">
                                            Explore<svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
                
                {/* Banner Carousel - Replicated from DesignerPage3 */}
                <section className="mb-20">
                    <div className="relative w-full h-[300px] sm:h-[400px] md:h-[500px] lg:h-[600px] shadow-2xl rounded-3xl overflow-hidden">
                        <img src="https://placehold.co/1920x1080/4A4A4A/FFFFFF?text=Banner+Carousel" alt="Banner" className="w-full h-full object-cover" />
                    </div>
                </section>

                {/* Product Gallery Section - Replicated from DesignerPage3 */}
                <section className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 max-w-7xl mx-auto">
                    <div className="flex justify-between items-baseline mb-6 border-b border-gray-200 pb-4">
                        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 capitalize">
                            Our Collection
                        </h2>
                        <span className="text-sm text-gray-500">12 items shown</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                        {[1, 2, 3, 4, 5, 6, 7, 8].map(id => (
                            <div key={id} className="bg-gray-50 p-4 rounded-lg shadow-sm">
                                <img src={`https://placehold.co/400x500/F0F0F0/333333?text=Product+${id}`} alt={`Product ${id}`} className="w-full h-64 object-cover rounded-md mb-4" />
                                <h3 className="font-semibold text-gray-800 text-lg">Product Title {id}</h3>
                                <p className="text-gray-600 mt-1">$XX.XX</p>
                                <button className="mt-3 w-full px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-700 transition-colors">Add to Cart</button>
                            </div>
                        ))}
                    </div>
                </section>
            </main>

            <footer className="bg-gray-900 text-white text-center p-6 mt-8">
                <p>&copy; 2025 Banner Store. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default Template3;